import { useCallback, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { Dropdown, Modal, Table, Typography } from 'antd'
import type { MenuProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Key } from 'antd/es/table/interface'
import moment from 'moment'
import styled from 'styled-components'

import { colours, sizes } from '@qflow/theme'

import { icons, UserAvatar } from 'portal/lib/primitives'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { deleteReading } from 'portal/store/utilities/readings/actions'

import { UTILITIES } from 'portal/components/Utilities/common'
import { Meter } from 'portal/components/Utilities/Meters/Interface'
import { Reading, ReadingForTable } from './Interface'
import { EditReadingModal } from './EditReadingModal'

interface ReadingsTableProps {
  meters: Meter[]
  readings: Reading[]
  utilityType: string
  selectedMeterId?: string
  readingsReadOnly?: boolean
  readingsReload: () => void
}

export const ReadingsTable = ({
  meters,
  readings,
  utilityType,
  selectedMeterId,
  readingsReadOnly = false,
  readingsReload
}: ReadingsTableProps) => {
  const [readingForEdit, setReadingForEdit] = useState<Reading | null>(null)
  const [isEditReadingModalOpen, setIsEditReadingModalOpen] = useState(false)

  const { accountId, projectId } = useParams<{
    accountId: string
    projectId: string
  }>()
  const dispatch = useDispatch()
  const { Title } = Typography

  const projectUsers = useSelector(
    (state: RootStateOrAny) => state.projectUsers
  )

  type User = {
    userId: string
    displayName: string
  }

  const users: User[] = Object.values(projectUsers.users)

  const filteredReadings = selectedMeterId
    ? readings.filter(reading => reading.meterId === selectedMeterId)
    : readings

  // Map keys to filderedReadings
  const readingsWithKeys: ReadingForTable[] = []
  filteredReadings.forEach(reading => {
    readingsWithKeys.push({ ...reading, key: reading.readingId as string })
  })

  // Unique user ids
  const uniqueUserIds = new Set(readings.map(reading => reading.updatedBy))

  const EV_READING_DELETE = 'frontend_utilities_readings_reading_deleted'
  const trackReadingDelete = useCallback(
    readingId => {
      dispatch(
        trackMixpanel(EV_READING_DELETE, {
          project_id: projectId,
          account_id: accountId,
          reading_id: readingId
        })
      )
    },
    [accountId, dispatch, projectId]
  )

  const handleDeleteReading = (
    readingId: string,
    meterId: string,
    eTag: string
  ) => {
    Modal.confirm({
      title: 'Delete meter reading',
      content: 'This operation cannot be undone. Are you sure?',
      autoFocusButton: null,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        handleConfirmDelete(readingId, meterId, eTag)
      }
    })
  }

  const handleConfirmDelete = (
    readingId: string,
    meterId: string,
    eTag: string
  ) => {
    dispatch(
      deleteReading(
        accountId,
        projectId,
        meterId,
        readingId as string,
        eTag as string,
        readingsReload
      )
    )
    trackReadingDelete(readingId)
  }

  const items = (reading: Reading): MenuProps['items'] => {
    return [
      {
        key: 'edit',
        label: (
          <div
            onClick={() => {
              // Clear the state before opening the modal
              setReadingForEdit(null)
              setTimeout(() => {
                setReadingForEdit(reading)
                setIsEditReadingModalOpen(true)
              }, 0)
            }}
          >
            Edit reading
          </div>
        )
      },
      {
        key: 'delete',
        label: (
          <RedText
            onClick={() =>
              handleDeleteReading(
                reading.readingId as string,
                reading.meterId,
                reading.eTag as string
              )
            }
          >
            Delete reading
          </RedText>
        )
      }
    ]
  }

  const columns: ColumnsType<Reading> = [
    {
      title: (
        <span>
          Reading{' '}
          {utilityType === UTILITIES.ELECTRICITY ? (
            <span>(kWh)</span>
          ) : (
            <span>
              (m<sup>3</sup>)
            </span>
          )}
        </span>
      ),
      dataIndex: 'actualValue',
      key: 'actualValue',
      width: '8.5rem',
      render: (text: string) => <b>{text}</b>,
      sorter: (a: Reading, b: Reading) => a.actualValue - b.actualValue
    },
    {
      title: 'Reading Date',
      dataIndex: 'readingDateTime',
      key: 'readingDateTime',
      width: '8.5rem',
      render: (text: string) => moment(text).format('DD/MM/YYYY'),
      sorter: (a: Reading, b: Reading) =>
        a.readingDateTime.localeCompare(b.readingDateTime)
    },
    {
      title: 'Meter name',
      dataIndex: 'meterId',
      key: 'meterName',
      render: (text: string) =>
        (meters.find((meter: Meter) => meter.meterId === text) as Meter)
          .meterName,
      sorter: (a: Reading, b: Reading) =>
        (
          meters.find((meter: Meter) => meter.meterId === a.meterId) as Meter
        ).meterName.localeCompare(
          (meters.find((meter: Meter) => meter.meterId === b.meterId) as Meter)
            .meterName
        )
    },
    {
      title: 'Meter reference',
      dataIndex: 'meterId',
      key: 'meterReference',
      width: '12.5rem',
      render: (text: string) =>
        (meters.find((meter: Meter) => meter.meterId === text) as Meter)
          .meterReference,
      sorter: (a: Reading, b: Reading) =>
        (
          meters.find((meter: Meter) => meter.meterId === a.meterId) as Meter
        ).meterReference.localeCompare(
          (meters.find((meter: Meter) => meter.meterId === b.meterId) as Meter)
            .meterReference
        )
    },
    {
      title: 'Status',
      dataIndex: 'isEstimated',
      key: 'isEstimated',
      width: '5.75rem',
      render: (value: boolean) =>
        value ? <Estimate>Estimate</Estimate> : <Actual>Actual</Actual>,
      filters: [
        { text: 'Actual', value: false },
        { text: 'Estimate', value: true }
      ],
      onFilter: (value: boolean | Key, record: Reading) =>
        record.isEstimated === (value as boolean)
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      sorter: (a: Reading, b: Reading) => a.notes.localeCompare(b.notes)
    },
    {
      title: 'Added by',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      width: '6.75rem',
      align: 'center',
      render: (text: string) => {
        const matchedUser: any = users
          ? users.find((user: any) => user.userId === text)
          : null
        return matchedUser ? (
          <UserAvatar displayName={matchedUser.displayName} />
        ) : (
          <UserAvatar displayName={'Qflow System'} />
        )
      },
      filters: Array.from(uniqueUserIds)
        .map(userId => {
          const user = users.find(user => user.userId === userId)
          return {
            text: user?.displayName || '',
            value: userId || ''
          }
        })
        .sort((a, b) => a.text.localeCompare(b.text)),
      onFilter: (value, record: Reading) => record.updatedBy === value
    }
  ]

  if (!readingsReadOnly) {
    // If the user has permission to edit/delete readings, add the last column
    columns.push({
      title: '',
      key: 'action',
      align: 'right',
      width: '2rem',
      render: (text: string, record: Reading) => (
        <Dropdown
          menu={{ items: items(record) }}
          align={{ offset: [21, 4] }}
          placement="topRight"
          arrow={{ pointAtCenter: true }}
          trigger={['click']}
        >
          <MoreActionsWrapper>
            <icons.Ellipsis colour={colours.CORE_NAVY} size={sizes.SMALL} />
          </MoreActionsWrapper>
        </Dropdown>
      )
    })
  }

  return (
    <ReadingTableSection>
      <TitleBar>
        <Title level={4}>Meter readings</Title>
      </TitleBar>
      <Table
        id="readings-table"
        dataSource={readingsWithKeys}
        columns={columns}
        pagination={false}
        locale={{ emptyText: 'There are no readings for the selected meter.' }}
      />
      {readingForEdit && (
        <EditReadingModal
          reading={readingForEdit}
          readings={readings.filter(
            reading =>
              reading.meterId === readingForEdit.meterId &&
              !reading.isEstimated &&
              reading.readingId !== readingForEdit.readingId
          )}
          selectedUtility={utilityType}
          isOpen={isEditReadingModalOpen}
          onCancel={() => setIsEditReadingModalOpen(false)}
          readingsReload={readingsReload}
        />
      )}
    </ReadingTableSection>
  )
}

const ReadingTableSection = styled.div`
  padding: 1.25rem 1.25rem 1.5rem 1.375rem;
  margin: 0 0 2rem -0.5rem;
  box-shadow: 0px 1px 3px 0px #00000026;
  background-color: ${colours.WHITE};
  border-radius: 10px;

  h4 {
    font-size: 1.125rem;
    margin: 0 0 1rem;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td,
  .ant-table tfoot > tr > th,
  .ant-table tfoot > tr > td {
    padding: 0.75rem;
  }

  .ant-table-thead > tr > th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &[aria-label='Meter name'] {
      min-width: 8rem;
    }

    .ant-table-column-title {
      span > span,
      sup {
        text-transform: none;
        letter-spacing: initial;
      }
    }
  }

  .ant-table-tbody > tr > td {
    word-break: break-word;
  }
`
const TitleBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`
const Estimate = styled.span`
  color: ${colours.CONSTRUCTION_ORANGE};
`
const Actual = styled.span`
  color: ${colours.GREEN};
`
const MoreActionsWrapper = styled.div`
  height: 2rem;
  width: 2rem;
  margin: 0 0 0 -1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  &:hover {
    background-color: ${colours.WHITE_10};
  }
  &:active {
    background-color: ${colours.WHITE_10};
  }
`
const RedText = styled.span`
  color: ${colours.RED};
  cursor: pointer;
`
